import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { addMinutes, parse } from 'date-fns';
import api from '../../api/client';
import ConfirmDialog from '../common/ConfirmDialog';
import { TimeSelect } from '../common/DateTimePicker';

const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const TIME_SLOTS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = (i % 2) * 30;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
});

const REPEAT_OPTIONS = [
  { value: 'specific', label: 'Día específico' },
  { value: 'weekdays', label: 'Lunes a Viernes' },
  { value: 'saturday', label: 'Sábado' },
  { value: 'sunday', label: 'Domingo' },
  { value: 'daily', label: 'Todos los días' },
];

const PATTERN_DAYS = {
  specific: null,
  weekdays: [1, 2, 3, 4, 5],
  saturday: [6],
  sunday: [0],
  daily: [0, 1, 2, 3, 4, 5, 6],
};

export default function ScheduleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [schedule, setSchedule] = useState([]);
  const [show, setShow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingSlot, setEditingSlot] = useState(null);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteSlot, setDeleteSlot] = useState(null);
  const [draggedSlotId, setDraggedSlotId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  useEffect(() => {
    if (id) {
      fetchShow();
      fetchSchedule();
    }
  }, [id]);

  const fetchShow = async () => {
    try {
      const { data } = await api.get(`/api/shows/${id}`);
      setShow(data);
    } catch (err) {
      console.error('[ScheduleEditor] Failed to fetch show:', err);
    }
  };

  const fetchSchedule = async () => {
    try {
      const { data } = await api.get(`/api/shows/${id}/schedule`);
      setSchedule(data || []);
    } catch (err) {
      console.error('[ScheduleEditor] Failed to fetch schedule:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlot = (dayIndex, time) => {
    setEditingSlot({
      id: null,
      day_of_week: dayIndex,
      day_pattern: 'specific',
      start_time: time,
      end_time: addMinutes(parse(time, 'HH:mm', new Date()), 60).toTimeString().slice(0, 5),
      is_active: true,
    });
    setShowSlotModal(true);
  };

  const handleEditSlot = (slot) => {
    setEditingSlot({ ...slot });
    setShowSlotModal(true);
  };

  const handleDeleteSlot = (slot) => {
    if (!slot.id) return;
    setDeleteSlot(slot);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api/shows/${id}/schedule/${deleteSlot.id}`);
      setSchedule(schedule.filter((s) => s.id !== deleteSlot.id));
    } catch (err) {
      console.error('[ScheduleEditor] Delete failed:', err);
    } finally {
      setConfirmOpen(false);
      setDeleteSlot(null);
    }
  };

  const handleSlotChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingSlot((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSlotSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        day: DAY_NAMES[editingSlot.day_of_week],
        day_pattern: editingSlot.day_pattern || 'specific',
        start_time: editingSlot.start_time,
        end_time: editingSlot.end_time,
        is_active: editingSlot.is_active !== false,
      };
      if (editingSlot.id) {
        const { data } = await api.put(`/api/shows/${id}/schedule/${editingSlot.id}`, payload);
        setSchedule(schedule.map((s) => (s.id === editingSlot.id ? data : s)));
      } else {
        const { data } = await api.post(`/api/shows/${id}/schedule`, payload);
        setSchedule([...schedule, data]);
      }
      setShowSlotModal(false);
      setEditingSlot(null);
    } catch (err) {
      console.error('[ScheduleEditor] Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const getSlotsFor = (dayIndex, time) => {
    return schedule.filter((s) => {
      if (s.day_pattern === 'specific') {
        return s.day_of_week === dayIndex && s.start_time === time;
      }
      const patternDays = PATTERN_DAYS[s.day_pattern];
      return patternDays && patternDays.includes(dayIndex) && s.start_time === time;
    });
  };

  // Mover un slot a otro día/hora arrastrándolo (snap a 30 min).
  // Convierte a "día específico" para que el destino sea predecible.
  const handleDropSlot = async (dayIndex, time) => {
    setDropTarget(null);
    const slot = schedule.find((s) => s.id === draggedSlotId);
    setDraggedSlotId(null);
    if (!slot) return;

    const start = parse(slot.start_time, 'HH:mm', new Date());
    const end = parse(slot.end_time, 'HH:mm', new Date());
    const durationMin = Math.max(30, Math.round((end - start) / 60000));
    const newEnd = addMinutes(parse(time, 'HH:mm', new Date()), durationMin)
      .toTimeString()
      .slice(0, 5);

    const payload = {
      day: DAY_NAMES[dayIndex],
      day_pattern: 'specific',
      start_time: time,
      end_time: newEnd,
      is_active: slot.is_active !== false,
    };
    try {
      const { data } = await api.put(`/api/shows/${id}/schedule/${slot.id}`, payload);
      setSchedule(schedule.map((s) => (s.id === slot.id ? data : s)));
    } catch (err) {
      console.error('[ScheduleEditor] Move slot failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <p className="text-dark-400">Cargando programación...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">Editor de Programación</h1>
          {show && <p className="text-sm text-dark-400 mt-1">{show.name}</p>}
        </div>
        <Link to="/shows" className="btn btn-secondary btn-sm">
          Volver a Series
        </Link>
      </div>

      <div className="overflow-x-auto rounded-xl bg-dark-850 border border-dark-700">
        <table className="table">
          <thead>
            <tr>
              <th className="w-32">Hora</th>
              {DAYS.map((day, i) => (
                <th key={day} className="w-28 text-center">
                  {day.slice(0, 3)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TIME_SLOTS.map((time) => (
              <tr key={time}>
                <td className="text-xs text-dark-400">{time}</td>
                {DAYS.map((day, dayIndex) => {
                  const slots = getSlotsFor(dayIndex, time);
                  const hour = parseInt(time.split(':')[0], 10);
                  const isCurrentHour = new Date().getHours() === hour;
                  return (
                    <td
                      key={`${dayIndex}-${time}`}
                      className={`relative h-12 cursor-pointer border-l border-dark-800 text-center align-text-bottom ${
                        slots.length > 0 ? 'bg-primary-900/20' : isCurrentHour ? 'bg-dark-800/30' : ''
                      } ${
                        dropTarget === `${dayIndex}-${time}`
                          ? 'bg-primary-700/30 ring-2 ring-primary-500'
                          : 'hover:bg-dark-800/50'
                      }`}
                      onClick={() => slots.length === 0 && handleAddSlot(dayIndex, time)}
                      onDragOver={(e) => {
                        if (draggedSlotId) {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          setDropTarget(`${dayIndex}-${time}`);
                        }
                      }}
                      onDragLeave={() =>
                        setDropTarget((cur) => (cur === `${dayIndex}-${time}` ? null : cur))
                      }
                      onDrop={(e) => {
                        e.preventDefault();
                        if (draggedSlotId) handleDropSlot(dayIndex, time);
                      }}
                      title={slots.length > 0 ? `${slots[0].start_time} - ${slots[0].end_time}` : `Añadir slot para ${day}`}
                    >
                      {slots.map((slot) => (
                        <div
                          key={slot.id}
                          draggable
                          onDragStart={(e) => {
                            e.stopPropagation();
                            e.dataTransfer.setData('text/plain', slot.id);
                            e.dataTransfer.effectAllowed = 'move';
                            setDraggedSlotId(slot.id);
                          }}
                          onDragEnd={() => {
                            setDraggedSlotId(null);
                            setDropTarget(null);
                          }}
                          className="flex h-full min-h-[48px] flex-col justify-center gap-0.5 px-1 cursor-grab active:cursor-grabbing"
                          title="Arrastralo a otro día/hora para moverlo"
                        >
                          <span className="truncate text-xs font-medium text-primary-400">
                            {slot.start_time}-{slot.end_time}
                          </span>
                          {slot.day_pattern && slot.day_pattern !== 'specific' && (
                            <span className="text-xs text-dark-500">
                              {REPEAT_OPTIONS.find((r) => r.value === slot.day_pattern)?.label}
                            </span>
                          )}
                          {slot.id && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditSlot(slot);
                              }}
                              className="text-xs text-dark-400 hover:text-dark-200"
                              title="Editar slot"
                            >
                              ✎
                            </button>
                          )}
                        </div>
                      ))}
                      {slots.length > 0 && slots[0].id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSlot(slots[0]);
                          }}
                          className="absolute top-0.5 right-0.5 text-xs text-error-400 hover:text-error-300"
                          title="Eliminar slot"
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-dark-500">
        💡 Arrastrá un slot a otro día/hora para moverlo (se guarda solo). Se convierte a "día específico" al moverlo. Podés usar la cuadrícula con scroll horizontal en pantallas chicas.
      </p>

      <div className="rounded-xl bg-dark-850 border border-dark-700 p-4">
        <h3 className="text-sm font-medium text-dark-300 mb-2">Entradas de Programación</h3>
        <div className="space-y-2">
          {schedule.length === 0 ? (
            <p className="text-sm text-dark-500">No hay entradas. Haz clic en una celda de la cuadrícula para añadir una.</p>
          ) : (
            <div className="space-y-1">
              {schedule
                .slice()
                .sort((a, b) => (a.day_of_week - b.day_of_week) || a.start_time.localeCompare(b.start_time))
                .map((slot) => (
                  <div key={slot.id} className="flex items-center justify-between rounded-lg bg-dark-800/50 px-3 py-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-medium w-14 text-dark-400">
                        {slot.day_pattern && slot.day_pattern !== 'specific'
                          ? REPEAT_OPTIONS.find((r) => r.value === slot.day_pattern)?.label?.split(' ')[0]
                          : DAYS[slot.day_of_week]?.slice(0, 3)}
                      </span>
                      <span className="text-sm text-dark-100">{slot.start_time} – {slot.end_time}</span>
                      {slot.day_pattern && slot.day_pattern !== 'specific' && (
                        <span className="text-xs text-dark-500">
                          ({REPEAT_OPTIONS.find((r) => r.value === slot.day_pattern)?.label})
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditSlot(slot)}
                        className="text-xs text-dark-400 hover:text-dark-200"
                        title="Editar"
                      >
                        ✎
                      </button>
                      <button
                        onClick={() => handleDeleteSlot(slot)}
                        className="text-xs text-error-400 hover:text-error-300"
                        title="Eliminar"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal de slot */}
      {showSlotModal && editingSlot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-xl bg-dark-800 p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-dark-100 mb-4">
              {editingSlot.id ? 'Editar Slot de Programación' : 'Añadir Slot de Programación'}
            </h3>
            <form onSubmit={handleSlotSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Patrón</label>
                <select
                  name="day_pattern"
                  value={editingSlot.day_pattern || 'specific'}
                  onChange={handleSlotChange}
                  className="input"
                  disabled={!!editingSlot.id}
                >
                  {REPEAT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {(editingSlot.day_pattern === 'specific' || !editingSlot.day_pattern) && (
                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-1">Día</label>
                  <select
                    name="day_of_week"
                    value={editingSlot.day_of_week}
                    onChange={handleSlotChange}
                    className="input"
                    disabled={!!editingSlot.id}
                  >
                    {DAYS.map((day, i) => (
                      <option key={day} value={i}>{day}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-1">Inicio</label>
                  <TimeSelect value={editingSlot.start_time} onChange={(v) => setEditingSlot((p) => ({ ...p, start_time: v }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-1">Fin</label>
                  <TimeSelect value={editingSlot.end_time} onChange={(v) => setEditingSlot((p) => ({ ...p, end_time: v }))} />
                </div>
              </div>

              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={editingSlot.is_active !== false}
                  onChange={handleSlotChange}
                  className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
                />
                <label htmlFor="is_active" className="text-sm text-dark-300">Activo</label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-dark-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowSlotModal(false);
                    setEditingSlot(null);
                  }}
                  className="btn btn-secondary btn-sm"
                >
                  Cancelar
                </button>
                <button type="submit" disabled={saving} className="btn btn-primary btn-sm">
                  {saving ? 'Guardando...' : 'Guardar Slot'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar Slot de Programación"
        message="¿Seguro que quieres eliminar este slot de programación?"
        danger
        onConfirm={confirmDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
